import { useState, useCallback } from 'react';
import axios from 'axios';

/**
 * Custom hook for managing the transaction import workflow.
 */
export const useImport = (activeProject, isEditor, fetchTransactions, fetchAccounts) => {
  const [file, setFile] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [dragging, setDragging] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [tempData, setTempData] = useState(null);
  const [loading, setLoading] = useState(false);

  const onImport = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!isEditor) return;
    
    if (!selectedAccount) {
      alert('Please select a target bank account first.');
      return;
    }
    if (!file) {
      alert('Please select a file to import.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', selectedAccount);
    setLoading(true);
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/transactions/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportSummary(res.data.summary);
      setTempData({ transactions: res.data.transactions, tempFile: res.data.tempFile });
      setShowSummary(true);
      setFile(null);
    } catch (err) { 
      alert('Import failed during parsing'); 
      console.error(err);
    } finally { 
      setLoading(false); 
    }
  }, [activeProject?._id, isEditor, file, selectedAccount]);

  const onFinalize = useCallback(async () => {
    if (!tempData || !isEditor) return;
    setLoading(true);
    try {
      await axios.post(`/api/projects/${activeProject._id}/transactions/import/finalize`, {
        transactions: tempData.transactions,
        tempFile: tempData.tempFile,
        accountId: selectedAccount,
        summary: importSummary
      });
      setShowSummary(false);
      setTempData(null);
      fetchTransactions();
      fetchAccounts();
      alert('Import finalized successfully');
    } catch (err) { 
      alert('Failed to finalize import'); 
      console.error(err);
    } finally { 
      setLoading(false); 
    }
  }, [activeProject?._id, isEditor, tempData, selectedAccount, fetchTransactions, fetchAccounts]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragging(true);
    else if (e.type === 'dragleave') setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  return {
    file, setFile,
    selectedAccount, setSelectedAccount,
    dragging,
    importSummary,
    showSummary, setShowSummary,
    tempData, setTempData,
    loading,
    onImport,
    onFinalize,
    handleDrag,
    handleDrop
  };
};
